import { Button, Flex, Link, Text, Title } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import { ReadInstructionButton } from "@/molecules/ReadInstructionButton"
import { AboutSection } from "../../molecules/AboutSection"
import { ConnectButton } from "../../molecules/ConnectButton"
import { PrerequisitesSection } from "../../molecules/PrerequisitesSection"
import { HowToUseCIIntegration } from "../HowToUseCIIntegration"
import { HowToUseCodingAgent } from "../HowToUseCodingAgent"
import { HowToUseKodemCLI } from "../HowToUseKodemCLI"
import { HowToUseKodemMCP } from "../HowToUseKodemMCP"
import { LinkIcon } from "./LinkIcon"

import styles from "./ItemIntegration.module.scss"

import type {
  ConnectPageInfo,
  SectionInfo,
  SubmitButtonInfo
} from "@/api/useIntegrations.types"
import type { ItemIntegrationProps } from "./ItemIntegration.types"

export const ItemIntegration = ({
  name,
  connectPage,
  extraInfo,
  isKodemCIIntegration,
  isKodemCLIIntegration,
  isKodemMCPIntegration,
  codingAgent
}: ItemIntegrationProps) => {
  const { logger, EVENTS } = useLogger()
  const { submitButton, sections = [] }: ConnectPageInfo = connectPage ?? {}
  const [firstSection, secondSection, howToUse] = sections
  const {
    label: labelSubmitButton,
    description: descriptionSubmitButton,
    link: linkSubmitButton,
    note: noteSubmitButton,
    connect: connectSubmitButton,
    disabled: disabledSubmitButton,
    icon: iconSubmitButtonKey = "default"
  }: Partial<SubmitButtonInfo> = submitButton ?? {}

  const showConnectButton = connectSubmitButton
  const onConnectClick = () => {
    logger.info(EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.OPENED_CONNECT, { name })
  }

  return (
    <>
      {firstSection && <AboutSection section={firstSection} hasDivider />}
      {secondSection && (
        <>
          <PrerequisitesSection section={secondSection} />
          {secondSection.additional?.map(
            (section: SectionInfo, index: number) => (
              <PrerequisitesSection
                key={index}
                section={section}
                classes={{ title: styles.titlePrerequisitesAdditional }}
              />
            )
          )}
        </>
      )}
      <div className={styles.submitButtonSection}>
        <Flex vertical>
          {descriptionSubmitButton && (
            <Title level={5}>{descriptionSubmitButton}</Title>
          )}
          {noteSubmitButton && (
            <Text className={styles.note} type="secondary">
              {noteSubmitButton}
            </Text>
          )}
          {linkSubmitButton && !showConnectButton && (
            <div>
              <Button
                disabled={disabledSubmitButton}
                icon={<LinkIcon type={iconSubmitButtonKey} />}
                type="primary"
                href={linkSubmitButton}
                target="_blank"
                onClick={() => {
                  logger.info(
                    EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.OPENED_INSTRUCTION,
                    { name }
                  )
                }}
              >
                {labelSubmitButton}
              </Button>
            </div>
          )}
          {connectSubmitButton && (
            <Link to={connectSubmitButton}>
              <ConnectButton onClick={onConnectClick}>
                {labelSubmitButton}
              </ConnectButton>
            </Link>
          )}
        </Flex>

        {!howToUse && !!secondSection?.instructionsLink && (
          <ReadInstructionButton href={secondSection.instructionsLink} />
        )}
      </div>
      {isKodemCIIntegration && howToUse && (
        <HowToUseCIIntegration
          name={name}
          {...howToUse}
          extraInfo={extraInfo}
        />
      )}
      {isKodemCLIIntegration && howToUse && (
        <HowToUseKodemCLI
          name={name}
          {...howToUse}
          extraInfo={extraInfo}
          instructionsLink={secondSection?.instructionsLink}
        />
      )}
      {codingAgent && howToUse && (
        <HowToUseCodingAgent agent={codingAgent} {...howToUse} />
      )}
      {isKodemMCPIntegration && howToUse && <HowToUseKodemMCP {...howToUse} />}
    </>
  )
}
